import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  buildNewClaimDrawerSections,
  getNewClaimDrawerInitialValues,
  INewClaimDrawerLovs,
  INewClaimDrawerValues
} from '@common/constants';
import {
  useAddClaim,
  useGetPolicyPolicyPersons,
  useSearchPoliciesForClaimLov
} from '@common/slices';
import { IBaseDrawerProps, IClaimPolicyLovItem } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { RootState } from '@core/redux/store';
import DateService from '@core/services/DateService';

const NewClaimDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [allPolicies, setAllPolicies] = useState<IClaimPolicyLovItem[]>([]);
  const [values, setValues] = useState<INewClaimDrawerValues>(
    getNewClaimDrawerInitialValues()
  );

  // Query hooks
  const [searchPolicies] = useSearchPoliciesForClaimLov.useLazy();
  const { data: policyPersonsData, refetch: getPolicyPersons } =
    useGetPolicyPolicyPersons.useQuery({
      options: {
        skip: !values.policyId,
        variables: { policyID: values.policyId || '' }
      }
    });
  const [createClaim] = useAddClaim();

  const policyPersons = useMemo(() => {
    const allPersons =
      policyPersonsData?.production?.getPolicyPolicyPersons || [];

    // Filter to only include INSURED persons
    return allPersons.filter((person) => {
      const typeValue = person?.typeList;
      if (Array.isArray(typeValue)) {
        return typeValue.includes('INSURED') || typeValue.includes('Insured');
      }

      return typeValue === 'INSURED' || typeValue === 'Insured';
    });
  }, [policyPersonsData]);

  // Auto-select insured if there's only one
  useEffect(() => {
    if (
      values.policyId &&
      policyPersons.length === 1 &&
      !values.insuredId.length
    ) {
      setValues((prev) => ({
        ...prev,
        insuredId: policyPersons[0].id
      }));
    }
  }, [values.policyId, policyPersons, values.insuredId.length]);

  // Load initial policies on component mount
  useEffect(() => {
    const loadInitialPolicies = async () => {
      try {
        const result = await searchPolicies({
          variables: {
            pagination: { pageNumber: 1, pageSize: 50 },
            keyword: '' // Empty keyword to get recent policies
          }
        });

        const searchResult = result.data?.production?.searchPolicies;
        if (searchResult) {
          setAllPolicies(searchResult.items || []);
        }
      } catch (error) {
        console.error('Error loading initial policies:', error);
      }
    };

    loadInitialPolicies();
  }, []); // Remove searchPolicies from dependencies

  // Calculate date bounds for validation
  const calculateDateBounds = (effectiveDate?: string) => {
    if (!effectiveDate) return { minDate: null, maxDate: null };

    // Claim date: from effective date to declaration date
    return {
      minDate: effectiveDate,
      maxDate: new Date().toISOString()
    };
  };

  const handleSubmit = async () => {
    const createClaimInput = {
      policyId: values.policyId,
      insuredId: values.insuredId || '',
      declarationDate: new Date(
        new Date(values.declarationDate as Date).setHours(0, 0, 0, 0)
      ).toISOString(),

      claimDate: new Date(
        new Date(values.claimDate as Date).setHours(0, 0, 0, 0)
      ).toISOString()
    };
    const result = await createClaim({ createClaimInput });
    const claimId = result.data.claimManagement.createClaim;
    onSuccess(`/claims-management/claims/${claimId}`);
  };

  const handleChange = (
    fieldName: keyof INewClaimDrawerValues,
    value: any,
    _sectionIndex: number,
    updatedValues: INewClaimDrawerValues
  ) => {
    console.log(fieldName, value, _sectionIndex, updatedValues);
    const newValues = { ...updatedValues, [fieldName]: value };

    // Clear insured selection when policy changes
    if (fieldName === 'policyId') {
      newValues.insuredId = '';
    }
    console.log(newValues);
    setValues(newValues);
  };

  // Convert policies and persons to the expected format
  const policiesRecord: Record<string, string> = {};
  allPolicies.forEach((policy) => {
    if (policy.id) {
      policiesRecord[policy.id] = policy.policyNumber;
    }
  });

  const personsRecord = useMemo(() => {
    const record: Record<string, string> = {};
    policyPersons.forEach((person) => {
      console.log(person);
      if (person.id) {
        const fullName =
          `${person.firstName || ''} ${person.middleName || ''} ${person.lastName || ''}`.trim();
        record[person.id] = fullName;
      }
    });

    return record;
  }, [policyPersons]);
  console.log(personsRecord);
  const defaultLovs: INewClaimDrawerLovs = {
    policies: policiesRecord,
    insureds: personsRecord
  };

  const selectedPolicy = allPolicies.find((p) => p.id === values.policyId);
  const bounds = selectedPolicy
    ? calculateDateBounds(selectedPolicy.policyEffectiveDate)
    : undefined;

  const sections = useMemo(() => {
    console.log(defaultLovs);
    console.log(bounds);

    return buildNewClaimDrawerSections(
      values,
      defaultLovs,
      bounds
        ? {
            declarationDateMinDate: bounds.minDate
              ? DateService.toDateObject(bounds.minDate)
              : undefined,
            declarationDateMaxDate: bounds.maxDate
              ? DateService.toDateObject(bounds.maxDate)
              : undefined,
            claimDateMinDate: bounds.minDate
              ? DateService.toDateObject(bounds.minDate)
              : undefined,
            claimDateMaxDate: values.declarationDate
              ? new Date(values.declarationDate)
              : undefined
          }
        : undefined
    );
  }, [values, defaultLovs, bounds]);

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={handleSubmit}
      onChange={handleChange}
      submitButtonState={submitButtonState}
      onAutocompleteSearch={async (fieldName, value, pagination) => {
        if (fieldName === 'policyId') {
          const result = await searchPolicies({
            variables: {
              pagination,
              keyword: value
            }
          });

          const searchResult = result.data?.production?.searchPolicies;

          // Update the allPolicies state for other components to use
          setAllPolicies(searchResult.items || []);

          // Convert to options format for autocomplete
          const options: Record<string, string> = {};
          searchResult.items?.forEach((policy) => {
            if (policy.id) {
              options[policy.id] = policy.policyNumber;
            }
          });

          return {
            options,
            totalCount: parseInt(searchResult.paging?.totalCount || '0', 10)
          };
        }

        return Promise.resolve({ options: {}, totalCount: 0 });
      }}
    />
  );
};

export { NewClaimDrawer };
